/**
 * Client-side category detection service
 * Implements word-based matching algorithm matching Insights Platform approach
 */

import { supabase } from "@/lib/supabase";
import { ItemCategory } from "@/lib/types";

interface MarketItem {
  title: string;
  category: ItemCategory | null;
  subcategory: string | null;
}

interface DetectionResult {
  category: ItemCategory | "";
  type: string;
}

/**
 * Extract words from title (minimum 3 characters, alphanumeric only)
 * Matches Insights Platform word extraction logic
 */
function extractWords(title: string): string[] {
  return title
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z0-9]/g, ""))
    .filter((word) => word.length >= 3);
}

/**
 * Score a market item against the search title
 * Improved algorithm that prioritizes product-specific matches over brand names
 * - Product-specific words: +1 point each
 * - Brand names: +0.5 points each (lower weight)
 * - Product phrases (2-3 words): +5 points (high priority)
 * - Exact phrase match: +2 points
 */
function scoreItem(searchTitle: string, itemTitle: string): number {
  const searchWords = extractWords(searchTitle);
  const itemWords = extractWords(itemTitle);
  const itemTitleLower = itemTitle.toLowerCase();
  const searchTitleLower = searchTitle.toLowerCase();

  let score = 0;

  // Common brand names that are less important for matching
  const commonBrands = new Set([
    "samsung",
    "lg",
    "sony",
    "panasonic",
    "philips",
    "haier",
    "hisense",
    "apple",
    "hp",
    "dell",
    "lenovo",
    "acer",
    "asus",
    "toshiba",
    "sharp",
    "whirlpool",
    "electrolux",
    "bosch",
    "siemens",
    "ge",
    "frigidaire",
    "kenmore",
    "maytag",
  ]);

  // Count matching words with different weights
  let brandMatches = 0;
  let productMatches = 0;

  searchWords.forEach((searchWord) => {
    if (itemWords.includes(searchWord)) {
      // Brand names get lower weight (0.5 points)
      if (commonBrands.has(searchWord)) {
        score += 0.5;
        brandMatches++;
      } else {
        // Product-specific words get full weight (1 point)
        score += 1;
        productMatches++;
      }
    }
  });

  // If item only matches brand names and no product words, heavily penalize it
  // This ensures items with actual product matches win over brand-only matches
  if (brandMatches > 0 && productMatches === 0) {
    score *= 0.3; // Reduce brand-only matches to 30% of their score
  }

  // Check for multi-word product phrases (higher priority)
  // Extract 2-word and 3-word phrases from search title, excluding brand names
  const searchPhrases: string[] = [];
  const searchWordsArray = searchTitleLower
    .split(/\s+/)
    .map((w) => w.replace(/[^a-z0-9]/g, ""))
    .filter((w) => w.length >= 3 && !commonBrands.has(w)); // Exclude brand names from phrases

  // Generate 2-word phrases (product-specific, no brands)
  for (let i = 0; i < searchWordsArray.length - 1; i++) {
    const word1 = searchWordsArray[i];
    const word2 = searchWordsArray[i + 1];
    if (word1.length >= 3 && word2.length >= 3) {
      searchPhrases.push(`${word1} ${word2}`);
    }
  }

  // Generate 3-word phrases (product-specific, no brands)
  for (let i = 0; i < searchWordsArray.length - 2; i++) {
    const word1 = searchWordsArray[i];
    const word2 = searchWordsArray[i + 1];
    const word3 = searchWordsArray[i + 2];
    if (word1.length >= 3 && word2.length >= 3 && word3.length >= 3) {
      searchPhrases.push(`${word1} ${word2} ${word3}`);
    }
  }

  // Also check original title for phrase matches (handles numbers and special chars)
  const originalPhrases: string[] = [];
  const originalWords = searchTitleLower.split(/\s+/).filter((w) => {
    const normalized = w.replace(/[^a-z0-9]/g, "");
    return normalized.length >= 3 && !commonBrands.has(normalized);
  });

  for (let i = 0; i < originalWords.length - 1; i++) {
    originalPhrases.push(`${originalWords[i]} ${originalWords[i + 1]}`);
  }

  // Check for phrase matches (much higher weight - increased to +10)
  const allPhrases = [...searchPhrases, ...originalPhrases];
  allPhrases.forEach((phrase) => {
    // Normalize item title for matching (remove special chars, keep spaces)
    const itemTitleNormalized = itemTitleLower
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ");
    const phraseNormalized = phrase
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ");

    if (
      itemTitleNormalized.includes(phraseNormalized) &&
      phraseNormalized.trim().length >= 5
    ) {
      // Product phrases get very high bonus (+10 points)
      // This ensures "chest freezer" decisively beats "Samsung" alone
      score += 10;
    }
  });

  // Bonus for exact phrase matches (+2) - keep this for very close matches
  if (
    itemTitleLower.includes(searchTitleLower) &&
    searchTitleLower.length >= 5
  ) {
    score += 2;
  }

  return score;
}

/**
 * Detect category and subcategory from item title using word-based matching
 * Matches Insights Platform detection approach
 */
export async function detectCategoryFromTitle(
  title: string,
): Promise<DetectionResult> {
  const trimmedTitle = title.trim();

  // Minimum length check (5 characters like Insights Platform)
  if (trimmedTitle.length < 5) {
    return { category: "", type: "" };
  }

  try {
    // Extract keywords from title for better matching
    const searchWords = extractWords(trimmedTitle);

    console.log("Category detection starting:", {
      title: trimmedTitle,
      searchWords,
      searchWordsCount: searchWords.length,
    });

    // Build search query - try to find items with matching words in title
    let items;
    let error;

    // If we have search words, filter by them first
    if (searchWords.length > 0) {
      // Build OR query for keywords (search for items containing any of the keywords)
      const keywordQueries = searchWords
        .map((keyword) => `title.ilike.%${keyword}%`)
        .join(",");
      const { data, error: queryError } = await supabase
        .from("items")
        .select("title, category, subcategory")
        .eq("status", "approved")
        .not("category", "is", null)
        .or(keywordQueries)
        .order("created_at", { ascending: false }) // Get most recent items first
        .limit(3000); // Increased to 3000 most recent items
      items = data;
      error = queryError;

      // If no matches found with keywords, fall back to searching all items
      if ((!items || items.length === 0) && !error) {
        console.log(
          "No items found with keyword filter, falling back to all items",
        );
        const { data: allItems, error: allError } = await supabase
          .from("items")
          .select("title, category, subcategory")
          .eq("status", "approved")
          .not("category", "is", null)
          .order("created_at", { ascending: false }) // Get most recent items first
          .limit(2000); // Increased to 2000 most recent items
        items = allItems;
        error = allError;
      }
    } else {
      // No search words, get all items (most recent first)
      const { data, error: queryError } = await supabase
        .from("items")
        .select("title, category, subcategory")
        .eq("status", "approved")
        .not("category", "is", null)
        .order("created_at", { ascending: false }) // Get most recent items first
        .limit(3000); // Increased to 3000 most recent items
      items = data;
      error = queryError;
    }

    if (error) {
      console.error("Error fetching items for category detection:", error);
      return { category: "", type: "" };
    }

    if (!items || items.length === 0) {
      console.log("No items found in database");
      return { category: "", type: "" };
    }

    console.log(`Found ${items.length} items to score`);

    // Score each item
    const scoredItems = items
      .map((item) => {
        const score = scoreItem(trimmedTitle, item.title || "");
        return {
          item: item as MarketItem,
          score,
        };
      })
      .filter((scored) => scored.score > 0) // Only keep items with matches
      .sort((a, b) => b.score - a.score); // Sort by score descending

    if (scoredItems.length === 0) {
      console.log("No items scored above 0");
      return { category: "", type: "" };
    }

    // Log top 5 matches for debugging
    console.log(
      "Top 5 scored items:",
      scoredItems.slice(0, 5).map((s) => ({
        title: s.item.title,
        category: s.item.category,
        subcategory: s.item.subcategory,
        score: s.score,
      })),
    );

    // Get the highest-scoring match (bestMatch)
    const bestMatch = scoredItems[0].item;

    const result: DetectionResult = {
      category: (bestMatch.category as ItemCategory) || "",
      type: bestMatch.subcategory || "",
    };

    console.log("Category detection result:", {
      title: trimmedTitle,
      bestMatch: {
        title: bestMatch.title,
        category: result.category,
        type: result.type,
        score: scoredItems[0].score,
      },
      totalMatches: scoredItems.length,
    });

    return result;
  } catch (error) {
    console.error("Error in category detection:", error);
    return { category: "", type: "" };
  }
}
